import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import { useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useState, useEffect, useMemo, type ReactNode } from "react";
import { ThumbsUp, ThumbsDown, MessageSquare, CheckCircle, XCircle, ArrowLeft, Search } from "lucide-react";
import { format } from "date-fns";
import { useToast } from "@/hooks/use-toast";
import { useLocation } from "wouter";
import ProductInsightsBoard from "@/components/product-insights-board";
import { motion } from "framer-motion";

type CachedResponse = {
  id: string;
  originalQuery: string;
  refinedQuery: string;
  agent: string | null;
  answer: string;
  sources: any;
  suggestedQuestions: any;
  votes: { up: number; down: number };
  comments: Array<{
    text: string;
    userId: string;
    username: string;
    createdAt: string;
  }>;
  allComments?: Array<{
    text: string;
    userId: string;
    username: string;
    createdAt: string;
    vote?: string;
    type: 'admin' | 'user_vote';
  }>;
  approved: boolean;
  createdAt: string;
  updatedAt: string;
  userComments?: Array<{
    text: string;
    userId: string;
    username: string;
    createdAt: string;
    vote?: string;
  }>;
};

type FeedbackStats = {
  agent: string;
  questions: number;
  upvotes: number;
  downvotes: number;
};

type AnimatedSectionProps = {
  children: ReactNode;
  delay?: number;
};

const AnimatedSection = ({ children, delay = 0 }: AnimatedSectionProps) => (
  <motion.div
    initial={{ opacity: 0, y: 24 }}
    whileInView={{ opacity: 1, y: 0 }}
    transition={{
      duration: 0.7,
      ease: [0.16, 1, 0.3, 1],
      delay,
    }}
    viewport={{ once: true, amount: 0.2 }}
  >
    {children}
  </motion.div>
);

export default function ResponseHub() {
  const { toast } = useToast();
  const [, setLocation] = useLocation();
  const [sortBy, setSortBy] = useState<string>("recent");
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState<string>("");
  const [commentDialogOpen, setCommentDialogOpen] = useState(false);
  const [selectedResponse, setSelectedResponse] = useState<CachedResponse | null>(null);
  const [commentText, setCommentText] = useState("");
  const [displayedCount, setDisplayedCount] = useState(20);

  // Get tab from URL query parameter
  const [location] = useLocation();
  const urlParams = new URLSearchParams(location.split('?')[1]);
  const defaultTab = urlParams.get('tab') === 'insights' ? 'insights' : 'responses';

  // Helper function to safely format dates
  const formatDate = (dateString: string | null | undefined, formatStr: string): string => {
    if (!dateString) return 'No date';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'Invalid date';
    return format(date, formatStr);
  };

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Reset displayed count when search or sort changes
  useEffect(() => {
    setDisplayedCount(20);
  }, [debouncedSearchQuery, sortBy]);

  // Fetch all cached responses with search
  const { data: responses, isLoading: responsesLoading, error: responsesError } = useQuery<CachedResponse[]>({
    queryKey: ['/api/admin/feedback', debouncedSearchQuery],
    queryFn: async () => {
      const url = debouncedSearchQuery && debouncedSearchQuery.trim()
        ? `/api/admin/feedback?search=${encodeURIComponent(debouncedSearchQuery.trim())}`
        : '/api/admin/feedback';

      console.log('Fetching responses from:', url);
      const response = await apiRequest('GET', url);
      const result = await response.json();

      console.log('API response:', result);

      // Ensure we always return an array
      if (!Array.isArray(result)) {
        console.error('API returned non-array response:', result);
        return [];
      }

      return result;
    },
  });

  // Fetch stats
  const { data: stats, isLoading: statsLoading } = useQuery<FeedbackStats[]>({
    queryKey: ['/api/admin/feedback/stats'],
  });

  // Add comment mutation
  const addCommentMutation = useMutation({
    mutationFn: async ({ id, text }: { id: string; text: string }) => {
      await apiRequest('POST', `/api/admin/feedback/${id}/comment`, { text });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/admin/feedback'] });
      toast({
        title: "Comment added",
        description: "Your feedback has been saved.",
      });
      setCommentDialogOpen(false);
      setCommentText("");
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to add comment",
        variant: "destructive",
      });
    },
  });

  // Toggle approval mutation
  const toggleApprovalMutation = useMutation({
    mutationFn: async ({ id, approved }: { id: string; approved: boolean }) => {
      await apiRequest('PUT', `/api/admin/feedback/${id}/approval`, { approved });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/admin/feedback'] });
      toast({
        title: "Approval updated",
        description: "Response approval status has been updated.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to update approval",
        variant: "destructive",
      });
    },
  });

  // Filter and sort responses
  const filteredResponses = useMemo(() => {
    if (!responses || !Array.isArray(responses)) {
      return [];
    }

    return responses
      .sort((a, b) => {
        switch (sortBy) {
          case "votes":
            const aNetVotes = (a.votes?.up || 0) - (a.votes?.down || 0);
            const bNetVotes = (b.votes?.up || 0) - (b.votes?.down || 0);
            return bNetVotes - aNetVotes;
          case "comments":
            return (b.userComments?.length || 0) - (a.userComments?.length || 0);
          case "recent":
          default:
            const aTime = a.createdAt ? new Date(a.createdAt).getTime() : 0;
            const bTime = b.createdAt ? new Date(b.createdAt).getTime() : 0;
            return (isNaN(bTime) ? 0 : bTime) - (isNaN(aTime) ? 0 : aTime);
        }
      });
  }, [responses, sortBy]);

  // Slice filtered responses to show only displayed count
  const displayedResponses = useMemo(() => {
    return filteredResponses.slice(0, displayedCount);
  }, [filteredResponses, displayedCount]);


  // Calculate total stats
  const totalStats = (stats || []).reduce(
    (acc, stat) => ({
      questions: acc.questions + stat.questions,
      upvotes: acc.upvotes + stat.upvotes,
      downvotes: acc.downvotes + stat.downvotes,
    }),
    { questions: 0, upvotes: 0, downvotes: 0 }
  );

  const handleAddComment = () => {
    if (!selectedResponse || !commentText.trim()) return;

    addCommentMutation.mutate({
      id: selectedResponse.id,
      text: commentText.trim(),
    });
  };

  const handleToggleApproval = (response: CachedResponse) => {
    toggleApprovalMutation.mutate({
      id: response.id,
      approved: !response.approved,
    });
  };

  return (
    <div
      className="min-h-screen bg-white"
      style={{
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
        background: '#ffffff',
        paddingTop: 'env(safe-area-inset-top, 0px)',
        paddingBottom: 'env(safe-area-inset-bottom, 0px)',
        paddingLeft: 'env(safe-area-inset-left, 0px)',
        paddingRight: 'env(safe-area-inset-right, 0px)'
      }}
    >
      <motion.header
        className="px-4 sm:px-6 h-14 border-b border-[#d0d0d0] bg-white sticky top-0 z-50"
        style={{
          paddingTop: 'calc(14px + env(safe-area-inset-top, 0px))',
          height: 'calc(56px + env(safe-area-inset-top, 0px))',
          paddingLeft: 'calc(16px + env(safe-area-inset-left, 0px))',
          paddingRight: 'calc(16px + env(safe-area-inset-right, 0px))'
        }}
        initial={{ opacity: 0, y: -16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
      >
        <div className="max-w-7xl mx-auto flex justify-between items-center h-full">
          <div className="flex items-center gap-4" style={{ textAlign: 'left' }}>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setLocation('/admin')}
              className="flex items-center gap-2 text-[#1a1a1a] hover:text-[#1a1a1a] hover:bg-[#f5f5f5] touch-manipulation"
              style={{ minHeight: '44px', fontSize: '16px', textAlign: 'left' }}
            >
              <ArrowLeft className="h-4 w-4" />
              <span className="hidden sm:inline">Back to Admin</span>
            </Button>
            <div className="w-8 h-8 border border-[#d0d0d0] bg-white rounded-md flex items-center justify-center flex-shrink-0">
              <span className="text-[#1a1a1a] text-sm font-bold">CT</span>
            </div>
            <div style={{ textAlign: 'left' }}>
              <h1 className="text-xl font-semibold text-[#1a1a1a]" style={{ textAlign: 'left' }}>Response Hub & Product Insights</h1>
              <p className="text-xs text-[#1a1a1a]" style={{ textAlign: 'left' }}>
                Review AI responses, manage feedback, and analyze product performance
              </p>
            </div>
          </div>
        </div>
      </motion.header>
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8" style={{ textAlign: 'left' }}>
        <Tabs defaultValue={defaultTab} className="w-full" style={{ textAlign: 'left' }}>
          <TabsList className="mb-6" style={{ minHeight: '44px' }}>
            <TabsTrigger
              value="responses"
              className="touch-manipulation"
              style={{ fontSize: '16px', minHeight: '44px', textAlign: 'center' }}
            >
              Response Hub
            </TabsTrigger>
            <TabsTrigger
              value="insights"
              className="touch-manipulation"
              style={{ fontSize: '16px', minHeight: '44px', textAlign: 'center' }}
            >
              Product Insights
            </TabsTrigger>
          </TabsList>

          <TabsContent value="insights" className="mt-0">
            <ProductInsightsBoard />
          </TabsContent>

          <TabsContent value="responses" className="mt-0">
            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8" style={{ textAlign: 'left' }}>
              <Card className="rounded-lg border border-[#d0d0d0] bg-white shadow-sm" style={{ textAlign: 'left' }}>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2" style={{ textAlign: 'left' }}>
                  <CardTitle className="text-sm font-medium text-[#1a1a1a]" style={{ textAlign: 'left' }}>Total Responses</CardTitle>
                  <MessageSquare className="h-4 w-4 text-[#1a1a1a] flex-shrink-0" />
                </CardHeader>
                <CardContent style={{ textAlign: 'left' }}>
                  <div className="text-2xl font-semibold text-[#1a1a1a]" style={{ textAlign: 'left' }}>{totalStats.questions}</div>
                </CardContent>
              </Card>
              <Card className="rounded-lg border border-[#d0d0d0] bg-white shadow-sm" style={{ textAlign: 'left' }}>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2" style={{ textAlign: 'left' }}>
                  <CardTitle className="text-sm font-medium text-[#1a1a1a]" style={{ textAlign: 'left' }}>Upvotes</CardTitle>
                  <ThumbsUp className="h-4 w-4 text-[#1a1a1a] flex-shrink-0" />
                </CardHeader>
                <CardContent style={{ textAlign: 'left' }}>
                  <div className="text-2xl font-semibold text-[#1a1a1a]" style={{ textAlign: 'left' }}>{totalStats.upvotes}</div>
                </CardContent>
              </Card>
              <Card className="rounded-lg border border-[#d0d0d0] bg-white shadow-sm" style={{ textAlign: 'left' }}>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2" style={{ textAlign: 'left' }}>
                  <CardTitle className="text-sm font-medium text-[#1a1a1a]" style={{ textAlign: 'left' }}>Downvotes</CardTitle>
                  <ThumbsDown className="h-4 w-4 text-[#1a1a1a] flex-shrink-0" />
                </CardHeader>
                <CardContent style={{ textAlign: 'left' }}>
                  <div className="text-2xl font-semibold text-[#1a1a1a]" style={{ textAlign: 'left' }}>{totalStats.downvotes}</div>
                </CardContent>
              </Card>
              <Card className="rounded-lg border border-[#d0d0d0] bg-white shadow-sm" style={{ textAlign: 'left' }}>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2" style={{ textAlign: 'left' }}>
                  <CardTitle className="text-sm font-medium text-[#1a1a1a]" style={{ textAlign: 'left' }}>Net Score</CardTitle>
                  <MessageSquare className="h-4 w-4 text-[#1a1a1a] flex-shrink-0" />
                </CardHeader>
                <CardContent style={{ textAlign: 'left' }}>
                  <div className="text-2xl font-semibold text-[#1a1a1a]" style={{ textAlign: 'left' }}>
                    {totalStats.upvotes - totalStats.downvotes}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Search and Filters */}
            <Card className="mb-6 rounded-lg border border-[#e0e0e0] bg-white shadow-sm" style={{ textAlign: 'left' }}>
              <CardHeader style={{ textAlign: 'left' }}>
                <CardTitle className="text-[#1a1a1a]" style={{ textAlign: 'left', marginBottom: '4px' }}>Search & Filter</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4" style={{ textAlign: 'left' }}>
                {/* Search Bar */}
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-[#1a1a1a] flex-shrink-0" />
                  <input
                    type="text"
                    placeholder="Search responses by query or content..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-[#d0d0d0] rounded-md bg-[#f5f5f5] text-[#1a1a1a] placeholder:text-[#b0b0b0] focus:outline-none focus:border-[#d0d0d0] focus:ring-1 focus:ring-[#d4d4d4]"
                    style={{
                      fontSize: '16px',
                      textAlign: 'left',
                      WebkitAppearance: 'none',
                      appearance: 'none',
                      minHeight: '44px',
                      padding: '12px 16px 12px 40px'
                    }}
                  />
                </div>

                {/* Sort Filter */}
                <div className="flex gap-4" style={{ textAlign: 'left' }}>
                  <div className="flex-1">
                    <Select value={sortBy} onValueChange={setSortBy}>
                      <SelectTrigger
                        data-testid="select-sort"
                        className="border border-[#d0d0d0] bg-white text-[#1a1a1a] touch-manipulation"
                        style={{
                          fontSize: '16px',
                          textAlign: 'left',
                          minHeight: '44px',
                          padding: '12px 16px'
                        }}
                      >
                        <SelectValue placeholder="Sort by" />
                      </SelectTrigger>
                      <SelectContent className="bg-white border border-[#d0d0d0]">
                        <SelectItem value="recent" className="text-[#1a1a1a] hover:bg-[#f5f5f5]" style={{ fontSize: '16px', minHeight: '44px' }} data-testid="sort-recent">Most Recent</SelectItem>
                        <SelectItem value="votes" className="text-[#1a1a1a] hover:bg-[#f5f5f5]" style={{ fontSize: '16px', minHeight: '44px' }} data-testid="sort-votes">Highest Votes</SelectItem>
                        <SelectItem value="comments" className="text-[#1a1a1a] hover:bg-[#f5f5f5]" style={{ fontSize: '16px', minHeight: '44px' }} data-testid="sort-comments">Most Commented</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Responses Table */}
            <Card className="rounded-lg border border-[#e0e0e0] bg-white shadow-sm" style={{ textAlign: 'left' }}>
              <CardHeader style={{ textAlign: 'left' }}>
                <CardTitle className="text-[#1a1a1a]" style={{ textAlign: 'left', marginBottom: '4px' }}>Response Library</CardTitle>
                <CardDescription className="text-[#1a1a1a]" style={{ textAlign: 'left', lineHeight: '1.5' }}>
                  {filteredResponses.length} responses
                </CardDescription>
              </CardHeader>
              <CardContent style={{ textAlign: 'left' }}>
                {responsesError ? (
                  <div className="text-center py-8 text-[#1a1a1a]" style={{ textAlign: 'center' }}>
                    Error loading responses: {responsesError.message}
                  </div>
                ) : responsesLoading ? (
                  <div className="text-center py-8 text-[#1a1a1a]" style={{ textAlign: 'center' }}>Loading...</div>
                ) : filteredResponses.length === 0 ? (
                  <div className="text-center py-8 text-[#1a1a1a]" style={{ textAlign: 'center' }}>
                    {searchQuery ? 'No responses found matching your search' : 'No responses found'}
                  </div>
                ) : (
                  <div className="overflow-x-auto" style={{ WebkitOverflowScrolling: 'touch' }}>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="text-[#1a1a1a]" style={{ textAlign: 'left', fontSize: '14px', padding: '12px 8px', whiteSpace: 'nowrap' }}>Query</TableHead>
                          <TableHead className="text-[#1a1a1a]" style={{ fontSize: '14px', padding: '12px 8px', whiteSpace: 'nowrap' }}>Agent</TableHead>
                          <TableHead className="text-center text-[#1a1a1a]" style={{ fontSize: '14px', padding: '12px 8px', whiteSpace: 'nowrap' }}>Votes</TableHead>
                          <TableHead className="text-center text-[#1a1a1a]" style={{ fontSize: '14px', padding: '12px 8px', whiteSpace: 'nowrap' }}>Comments</TableHead>
                          <TableHead className="text-center text-[#1a1a1a]" style={{ fontSize: '14px', padding: '12px 8px', whiteSpace: 'nowrap' }}>Status</TableHead>
                          <TableHead className="text-[#1a1a1a]" style={{ fontSize: '14px', padding: '12px 8px', whiteSpace: 'nowrap' }}>Date</TableHead>
                          <TableHead className="text-right text-[#1a1a1a]" style={{ fontSize: '14px', padding: '12px 8px', whiteSpace: 'nowrap' }}>Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {(displayedResponses && Array.isArray(displayedResponses) ? displayedResponses : []).map((response) => {
                          const netVotes = response.votes.up - response.votes.down;

                          return (
                            <TableRow
                              key={response.id}
                              data-testid={`response-row-${response.id}`}
                              className="hover:bg-[#f5f5f5] data-[state=selected]:bg-[#f5f5f5]"
                            >
                              <TableCell className="max-w-xs" style={{ padding: '12px 8px', fontSize: '14px', textAlign: 'left' }}>
                                <Dialog>
                                  <DialogTrigger asChild>
                                    <button
                                      className="text-left hover:underline text-sm text-[#1a1a1a] touch-manipulation"
                                      data-testid={`view-response-${response.id}`}
                                      style={{ textAlign: 'left', minHeight: '44px', lineHeight: '1.5', wordBreak: 'break-word' }}
                                    >
                                      {response.originalQuery && response.originalQuery.length > 60
                                        ? response.originalQuery.substring(0, 60) + '...'
                                        : response.originalQuery || 'No query'}
                                    </button>
                                  </DialogTrigger>
                                  <DialogContent
                                    className="max-w-3xl max-h-[80vh] overflow-y-auto bg-white border border-[#d0d0d0]"
                                    style={{
                                      paddingLeft: 'env(safe-area-inset-left, 0px)',
                                      paddingRight: 'env(safe-area-inset-right, 0px)',
                                      paddingTop: 'env(safe-area-inset-top, 0px)',
                                      paddingBottom: 'env(safe-area-inset-bottom, 0px)',
                                      WebkitOverflowScrolling: 'touch'
                                    }}
                                  >
                                    <DialogHeader style={{ textAlign: 'left' }}>
                                      <DialogTitle className="text-[#1a1a1a]" style={{ textAlign: 'left' }}>Response Details</DialogTitle>
                                    </DialogHeader>
                                    <div className="space-y-4" style={{ textAlign: 'left' }}>
                                      <div>
                                        <h4 className="font-semibold text-sm mb-1 text-[#1a1a1a]">Original Query</h4>
                                        <p className="text-sm text-[#1a1a1a]">{response.originalQuery}</p>
                                      </div>
                                      {response.refinedQuery !== response.originalQuery && (
                                        <div>
                                          <h4 className="font-semibold text-sm mb-1 text-[#1a1a1a]">Refined Query</h4>
                                          <p className="text-sm text-[#1a1a1a]">{response.refinedQuery}</p>
                                        </div>
                                      )}
                                      <div>
                                        <h4 className="font-semibold text-sm mb-1 text-[#1a1a1a]">Response</h4>
                                        <div className="text-sm p-3 rounded-md max-h-60 overflow-y-auto text-[#1a1a1a] bg-[#f5f5f5] border border-[#d0d0d0]">
                                          {response.answer}
                                        </div>

                                        {/* Customer Response Composer Buttons */}
                                        <div className="mt-3 flex flex-wrap gap-2">
                                          <p className="w-full text-xs font-semibold text-[#1a1a1a] mb-1">Refine Response:</p>
                                          <Button
                                            size="sm"
                                            variant="outline"
                                            className="text-xs border-[#d0d0d0] bg-white hover:bg-[#f5f5f5] text-[#1a1a1a] touch-manipulation"
                                            style={{ minHeight: '36px', fontSize: '13px' }}
                                            onClick={() => {
                                              navigator.clipboard.writeText(response.answer);
                                              toast({ title: "Copied to clipboard" });
                                            }}
                                          >
                                            Copy
                                          </Button>
                                          <Button
                                            size="sm"
                                            variant="outline"
                                            className="text-xs border-[#d0d0d0] bg-white hover:bg-[#f5f5f5] text-[#1a1a1a] touch-manipulation"
                                            style={{ minHeight: '36px', fontSize: '13px' }}
                                            onClick={() => {
                                              toast({ title: "Coming soon", description: "Make this response shorter" });
                                            }}
                                          >
                                            Make Shorter
                                          </Button>
                                          <Button
                                            size="sm"
                                            variant="outline"
                                            className="text-xs border-[#d0d0d0] bg-white hover:bg-[#f5f5f5] text-[#1a1a1a] touch-manipulation"
                                            style={{ minHeight: '36px', fontSize: '13px' }}
                                            onClick={() => {
                                              toast({ title: "Coming soon", description: "Rewrite in a more friendly tone" });
                                            }}
                                          >
                                            More Friendly
                                          </Button>
                                          <Button
                                            size="sm"
                                            variant="outline"
                                            className="text-xs border-[#d0d0d0] bg-white hover:bg-[#f5f5f5] text-[#1a1a1a] touch-manipulation"
                                            style={{ minHeight: '36px', fontSize: '13px' }}
                                            onClick={() => {
                                              toast({ title: "Coming soon", description: "Format for email" });
                                            }}
                                          >
                                            Email Ready
                                          </Button>
                                          <Button
                                            size="sm"
                                            variant="outline"
                                            className="text-xs border-[#d0d0d0] bg-white hover:bg-[#f5f5f5] text-[#1a1a1a] touch-manipulation"
                                            style={{ minHeight: '36px', fontSize: '13px' }}
                                            onClick={() => {
                                              toast({ title: "Coming soon", description: "Make more professional" });
                                            }}
                                          >
                                            Professional
                                          </Button>
                                          <Button
                                            size="sm"
                                            variant="outline"
                                            className="text-xs border-[#d0d0d0] bg-white hover:bg-[#f5f5f5] text-[#1a1a1a] touch-manipulation"
                                            style={{ minHeight: '36px', fontSize: '13px' }}
                                            onClick={() => {
                                              toast({ title: "Coming soon", description: "Make more casual" });
                                            }}
                                          >
                                            Casual
                                          </Button>
                                        </div>
                                      </div>
                                      {response.sources && response.sources.length > 0 && (
                                        <div>
                                          <h4 className="font-semibold text-sm mb-1 text-[#1a1a1a]">Sources</h4>
                                          <div className="space-y-1">
                                            {response.sources.map((source: any, index: number) => (
                                              <div key={index} className="text-xs text-[#1a1a1a]">
                                                {source.filename} ({Math.round((source.relevance || 0) * 100)}% match)
                                              </div>
                                            ))}
                                          </div>
                                        </div>
                                      )}
                                      <div>
                                        <h4 className="font-semibold text-sm mb-1 text-[#1a1a1a]">Votes</h4>
                                        <div className="flex items-center gap-4 text-sm">
                                          <span className="flex items-center gap-1 text-[#1a1a1a]">
                                            <ThumbsUp className="h-3 w-3" />
                                            <span className="text-[#1a1a1a]">{response.votes.up}</span>
                                          </span>
                                          <span className="flex items-center gap-1 text-[#1a1a1a]">
                                            <ThumbsDown className="h-3 w-3" />
                                            <span className="text-[#1a1a1a]">{response.votes.down}</span>
                                          </span>
                                          <span className="text-[#1a1a1a]">
                                            Net: {netVotes}
                                          </span>
                                        </div>
                                      </div>
                                      {response.userComments && response.userComments.length > 0 && (
                                        <div>
                                          <h4 className="font-semibold text-sm mb-1 text-[#1a1a1a]">User Comments</h4>
                                          <div className="space-y-2">
                                            {response.userComments.map((comment: any, index: number) => (
                                              <div key={index} className="text-xs p-2 rounded border border-[#d0d0d0] bg-[#f5f5f5]">
                                                <div className="flex items-center gap-2 mb-1">
                                                  <span className="font-medium text-[#1a1a1a]">{comment.username || 'Anonymous'}</span>
                                                  <span className="text-[#1a1a1a]">
                                                    {formatDate(comment.createdAt, 'MMM d, yyyy h:mm a')}
                                                  </span>
                                                  {comment.vote && (
                                                    <Badge variant={comment.vote === 'up' ? 'default' : 'destructive'} className="text-xs">
                                                      {comment.vote === 'up' ? '👍' : '👎'}
                                                    </Badge>
                                                  )}
                                                </div>
                                                <p className="text-[#1a1a1a]">{comment.comment || 'No comment text'}</p>
                                              </div>
                                            ))}
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  </DialogContent>
                                </Dialog>
                              </TableCell>
                              <TableCell style={{ padding: '12px 8px', fontSize: '14px', textAlign: 'left' }}>
                                <Badge variant="outline" className="border-[#d0d0d0] text-[#1a1a1a]" style={{ fontSize: '12px' }}>
                                  {response.agent || 'General'}
                                </Badge>
                              </TableCell>
                              <TableCell className="text-center" style={{ padding: '12px 8px', fontSize: '14px' }}>
                                <div className="flex items-center justify-center gap-1 text-sm tabular-nums" style={{ textAlign: 'center' }}>
                                  <span className="text-[#1a1a1a] font-medium">{response.votes.up}</span>
                                  <span className="text-[#1a1a1a]">/</span>
                                  <span className="text-[#1a1a1a] font-medium">{response.votes.down}</span>
                                  <span className="text-[#1a1a1a] ml-1">
                                    Net: <span className="text-[#1a1a1a]">{netVotes}</span>
                                  </span>
                                </div>
                              </TableCell>
                              <TableCell className="text-center" style={{ padding: '12px 8px', fontSize: '14px' }}>
                                <span className="text-sm tabular-nums font-medium text-[#1a1a1a]" style={{ textAlign: 'center' }}>
                                  {response.userComments?.length || 0}
                                </span>
                              </TableCell>
                              <TableCell className="text-center" style={{ padding: '12px 8px', fontSize: '14px' }}>
                                <Badge variant="outline" className="border-[#d0d0d0] text-[#1a1a1a]" style={{ fontSize: '12px' }}>
                                  {response.approved ? "Approved" : "Pending Review"}
                                </Badge>
                              </TableCell>
                              <TableCell style={{ padding: '12px 8px', fontSize: '14px', textAlign: 'left' }}>
                                <span className="text-sm text-[#1a1a1a]" style={{ textAlign: 'left' }}>
                                  {formatDate(response.createdAt, 'MMM d, yyyy')}
                                </span>
                              </TableCell>
                              <TableCell className="text-right" style={{ padding: '12px 8px', fontSize: '14px' }}>
                                <div className="flex items-center justify-end gap-2">
                                  <Dialog open={commentDialogOpen} onOpenChange={setCommentDialogOpen}>
                                    <DialogTrigger asChild>
                                      <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => setSelectedResponse(response)}
                                        className="border border-[#e0e0e0] text-[#1a1a1a] hover:bg-[#f5f5f5] hover:text-[#1a1a1a] touch-manipulation"
                                        data-testid={`add-comment-${response.id}`}
                                        style={{ minHeight: '44px', fontSize: '14px', textAlign: 'center' }}
                                      >
                                        <MessageSquare className="h-3 w-3 mr-1" />
                                        Comment
                                      </Button>
                                    </DialogTrigger>
                                    <DialogContent
                                      className="bg-white border border-[#d0d0d0]"
                                      style={{
                                        paddingLeft: 'env(safe-area-inset-left, 0px)',
                                        paddingRight: 'env(safe-area-inset-right, 0px)',
                                        paddingTop: 'env(safe-area-inset-top, 0px)',
                                        paddingBottom: 'env(safe-area-inset-bottom, 0px)',
                                        WebkitOverflowScrolling: 'touch'
                                      }}
                                    >
                                      <DialogHeader style={{ textAlign: 'left' }}>
                                        <DialogTitle className="text-[#1a1a1a]" style={{ textAlign: 'left' }}>Add Comment</DialogTitle>
                                        <DialogDescription className="text-[#1a1a1a]" style={{ textAlign: 'left', lineHeight: '1.5' }}>
                                          Add a comment for this response
                                        </DialogDescription>
                                      </DialogHeader>
                                      <div className="space-y-4" style={{ textAlign: 'left' }}>
                                        <div>
                                          <h4 className="font-semibold text-sm mb-1 text-[#1a1a1a]">Query</h4>
                                          <p className="text-sm text-[#1a1a1a]">
                                            {selectedResponse?.originalQuery}
                                          </p>
                                        </div>
                                        <div>
                                          <h4 className="font-semibold text-sm mb-1 text-[#1a1a1a]">Response</h4>
                                          <div className="text-sm p-3 rounded-md max-h-32 overflow-y-auto text-[#1a1a1a] bg-[#f5f5f5] border border-[#d0d0d0]">
                                            {selectedResponse?.answer}
                                          </div>
                                        </div>
                                        <div style={{ textAlign: 'left' }}>
                                          <Textarea
                                            placeholder="Enter your feedback or comment..."
                                            value={commentText}
                                            onChange={(e) => setCommentText(e.target.value)}
                                            rows={4}
                                            className="border border-[#d0d0d0] bg-white text-[#1a1a1a] placeholder:text-[#9b9b9b]"
                                            style={{
                                              fontSize: '16px',
                                              textAlign: 'left',
                                              WebkitAppearance: 'none',
                                              appearance: 'none',
                                              minHeight: '88px',
                                              padding: '12px 16px',
                                              lineHeight: '1.5'
                                            }}
                                          />
                                        </div>
                                        <div className="flex justify-end gap-2" style={{ textAlign: 'right' }}>
                                          <Button
                                            variant="outline"
                                            onClick={() => {
                                              setCommentDialogOpen(false);
                                              setCommentText("");
                                            }}
                                            className="text-[#1a1a1a] border-[#d0d0d0] hover:bg-[#f5f5f5] hover:text-[#1a1a1a] touch-manipulation"
                                            style={{ minHeight: '44px', fontSize: '16px', textAlign: 'center' }}
                                          >
                                            Cancel
                                          </Button>
                                          <Button
                                            onClick={handleAddComment}
                                            disabled={!commentText.trim() || addCommentMutation.isPending}
                                            className="border border-[#d0d0d0] bg-white hover:bg-[#f5f5f5] text-[#1a1a1a] touch-manipulation"
                                            style={{ minHeight: '44px', fontSize: '16px', textAlign: 'center' }}
                                          >
                                            {addCommentMutation.isPending ? "Adding..." : "Add Comment"}
                                          </Button>
                                        </div>
                                      </div>
                                    </DialogContent>
                                  </Dialog>
                                  <Button
                                    variant={response.approved ? "outline" : "default"}
                                    size="sm"
                                    onClick={() => handleToggleApproval(response)}
                                    disabled={toggleApprovalMutation.isPending}
                                    className={`touch-manipulation ${response.approved
                                      ? "text-[#1a1a1a] border border-[#d0d0d0] bg-white hover:bg-[#f5f5f5] hover:text-[#1a1a1a]"
                                      : "border border-[#d0d0d0] bg-white hover:bg-[#f5f5f5] text-[#1a1a1a]"
                                      }`}
                                    data-testid={`toggle-approval-${response.id}`}
                                    style={{ minHeight: '44px', fontSize: '14px', textAlign: 'center' }}
                                  >
                                    {response.approved ? (
                                      <>
                                        <XCircle className="h-3 w-3 mr-1" />
                                        Unapprove
                                      </>
                                    ) : (
                                      <>
                                        <CheckCircle className="h-3 w-3 mr-1" />
                                        Approve
                                      </>
                                    )}
                                  </Button>
                                </div>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>
                )}
                {filteredResponses.length > displayedCount && (
                  <div className="flex justify-center mt-6" style={{ textAlign: 'center' }}>
                    <Button
                      onClick={() => setDisplayedCount(prev => prev + 20)}
                      className="border border-[#d0d0d0] bg-white hover:bg-[#f5f5f5] text-[#1a1a1a] touch-manipulation"
                      style={{ minHeight: '44px', fontSize: '16px', textAlign: 'center' }}
                    >
                      Load More ({filteredResponses.length - displayedCount} remaining)
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
